/* ═══════════════════════════════════════════════════════════════════════════
   Sales by Channel / Order Source card  (ZAR-2371)

   ONE component for every place the Sales by Channel card appears — Snapshot and
   Sales, desktop and mobile (`variant`). A Channel / Source toggle swaps the
   donut and the breakdown table; the choice lives in Redux (reportFilters) so it
   survives navigation and is the same on both reports.

   Data: GET /sales/live/v2/by-channel — Channel view uses the screen's existing
   by-channel rows; Source view uses the same endpoint with type=source. The
   screen fetches BOTH up front, so the toggle never makes a request (SBS-03). Each
   row: marker, name, net sales (+ BE netSalesDelta), total collected, orders,
   avg order (BE todayAvgOrder). Rows sort by net sales; shares are 1 dp and sum
   to 100. The Total row is computed on the FE (and exported as-is). The donut
   centre is total net sales — identical in both views, same set of orders.
   ═══════════════════════════════════════════════════════════════════════════ */

import { useMemo } from 'react';
import DoughnutChart from '../charts/reusable/DoughnutChart';
import { ReportSectionExportButton } from '../ReportExportButton';
import SectionError from '../../SectionError';
import SectionEmpty from '../../SectionEmpty';
import { T, Bone } from '../../reportTokens';
import { formatGroupedNumber, formatMoneyForExport } from '../../../../utils/currency';
import { useSalesBreakdownView } from '../../../../redux/reportFilters/reportFiltersHooks';
import type { SalesBreakdownView } from '../../../../redux/reportFilters/reportFiltersTypes';
import type { OrderSource } from '../../../../redux/reports/clickHouse/clickHouseApi';
import type { LiveByChannelItem } from '../../../../interface/clickHouseInterface';
import KpiTooltip from '../../../KpiTooltip';
import { useLiveBySource } from './useLiveBySource';
import TruncatedText from './TruncatedText';
import {
    VIEW_COPY,
    buildRows,
    buildTotals,
    getSourceInfoText,
    type BreakdownRow,
} from './salesByChannelSourceData';

export interface SalesByChannelSourceCardProps {
    variant: 'desktop' | 'mobile';
    /** Channel-view rows (the screen's by-channel fetch, already in Redux). */
    items: LiveByChannelItem[];
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    /** Scope — drives the Source-view fetch. Today passes no dates. */
    locationId: string;
    startDate?: string;
    endDate?: string;
    orderSource?: OrderSource;
    /** Money formatter for the table and donut tooltip. */
    formatMoney: (v: number) => string;
    /** Compact formatter for the donut centre. */
    formatCenter?: (v: number) => string;
    /** Hover text on each change figure — the resolved comparison period. */
    comparisonHint?: string;
    exportDateRangeLabel: string;
    /** Filename stem; the view is appended ("…-channel" / "…-source"). */
    exportFilename: string;
    /** Fetches another outlet's rows for the multi-outlet export (`type=source` for the Source view). */
    fetchOutletItems: (locationId: string, view: SalesBreakdownView) => Promise<LiveByChannelItem[]>;
    countryCode?: string | null;
}

const VIEWS: SalesBreakdownView[] = ['channel', 'source'];

function ViewToggle({ value, onChange, compact }: { value: SalesBreakdownView; onChange: (v: SalesBreakdownView) => void; compact: boolean }) {
    return (
        <div role="tablist" aria-label="Breakdown view" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: 8, background: T.hover, flexShrink: 0 }}>
            {VIEWS.map((v) => {
                const active = v === value;
                return (
                    <button
                        key={v}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(v)}
                        style={{
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: 6,
                            padding: compact ? '4px 12px' : '6px 16px',
                            fontFamily: T.font,
                            fontSize: compact ? 12 : 13,
                            fontWeight: active ? 600 : 500,
                            color: active ? T.t1 : T.t3,
                            background: active ? T.surface : 'transparent',
                            boxShadow: active ? T.shadow : 'none',
                        }}
                    >
                        {VIEW_COPY[v].column}
                    </button>
                );
            })}
        </div>
    );
}

function Delta({ pct, hint, size }: { pct: number | null; hint?: string; size: number }) {
    if (pct == null || !Number.isFinite(pct)) {
        return <div title={hint} style={{ fontSize: size, color: T.t3, fontWeight: 500, marginTop: 2 }}>N/A</div>;
    }
    const zero = Math.abs(pct) < 0.05;
    const up = pct > 0;
    return (
        <div title={hint} style={{ fontSize: size, fontWeight: 500, marginTop: 2, color: zero ? T.t2 : up ? T.green : T.red }}>
            {!zero && (up ? '↑ ' : '↓ ')}{Math.abs(pct).toFixed(1)}%
        </div>
    );
}

const Marker = ({ color }: { color: string }) => (
    <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
);

export default function SalesByChannelSourceCard(props: SalesByChannelSourceCardProps) {
    const {
        variant, items, loading, error, onRetry,
        locationId, startDate, endDate, orderSource,
        formatMoney, formatCenter = formatMoney, comparisonHint,
        exportDateRangeLabel, exportFilename, fetchOutletItems, countryCode,
    } = props;
    const isMobile = variant === 'mobile';
    const [view, setView] = useSalesBreakdownView();
    const copy = VIEW_COPY[view];
    // Both views' rows are prefetched by the screen (SBS-03) — the toggle only picks one.
    const source = useLiveBySource({ locationId, startDate, endDate, orderSource });
    const viewItems = view === 'source' ? source.items : items;
    const viewLoading = view === 'source' ? source.loading : loading;
    const viewError = view === 'source' ? source.error : error;
    const viewRetry = view === 'source' ? source.retry : onRetry;

    const rows: BreakdownRow[] = useMemo(() => buildRows(view, viewItems), [view, viewItems]);
    const totals = useMemo(() => buildTotals(rows), [rows]);
    const donutData = useMemo(
        () => rows.filter((r) => r.netSales > 0).map((r) => ({ name: r.name, value: r.netSales, color: r.color, pct: Number(r.sharePct) })),
        [rows],
    );
    const fmtCount = (n: number) => formatGroupedNumber(n, countryCode, { maximumFractionDigits: 0 });

    const exportButton = rows.length > 0 && (
        <ReportSectionExportButton
            dateRangeLabel={exportDateRangeLabel}
            variant={variant}
            iconOnly={isMobile}
            title={copy.title}
            filename={`${exportFilename}-${view}`}
            // States which split the file holds — the CSV has no title row, so without this a
            // Source export was identifiable only by its first column header.
            metaFields={[['View', copy.column]]}
            buildExport={async (format, opts) => {
                const count = (n: number) => (format === 'pdf' ? fmtCount(n) : n);
                let exportRows = rows;
                if (opts?.locationId) {
                    // Multi-outlet export: same view, that outlet's rows; shares recomputed per outlet.
                    exportRows = buildRows(view, await fetchOutletItems(opts.locationId, view));
                }
                // Total row is FE-computed from the exported rows — the same numbers the card shows.
                const t = buildTotals(exportRows);
                return {
                    totalRow: true,
                    headers: [copy.column, 'Net Sales', 'Total Collected', 'Orders', 'Avg Order', 'Share %'],
                    rows: [
                        ...exportRows.map((r) => [r.name, formatMoneyForExport(r.netSales), formatMoneyForExport(r.totalCollected), count(r.orders), formatMoneyForExport(r.avgOrder), `${r.sharePct}%`]),
                        ['Total', formatMoneyForExport(t.netSales), formatMoneyForExport(t.totalCollected), count(t.orders), formatMoneyForExport(t.avgOrder), t.netSales !== 0 ? '100.0%' : '—'],
                    ],
                };
            }}
        />
    );

    /* ── Header ─────────────────────────────────────────────────────────── */
    const header = isMobile ? (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 2 }}>{copy.title}</div>
                    <div style={{ fontSize: 10, color: T.t3 }}>{copy.subtitle}</div>
                </div>
                {exportButton}
            </div>
            <div style={{ marginTop: 10 }}>
                <ViewToggle value={view} onChange={setView} compact />
            </div>
        </div>
    ) : (
        // Controls stay pinned top-right; only the title block may wrap onto more lines.
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>{copy.title}</div>
                <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{copy.subtitle}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                <ViewToggle value={view} onChange={setView} compact={false} />
                {exportButton}
            </div>
        </div>
    );

    const donutSize = isMobile ? 140 : 180;

    /* ── Body states ────────────────────────────────────────────────────── */
    let body: React.ReactNode;
    if (viewError) {
        body = <SectionError kpiName={copy.title.toLowerCase()} onRetry={viewRetry} minHeight={isMobile ? 180 : 240} />;
    } else if (viewLoading) {
        body = (
            <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <Bone w={donutSize} h={donutSize} r={donutSize / 2} />
                </div>
                {[0, 1, 2].map((i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: `1px solid ${T.divider}` }}>
                        <Bone w={10} h={10} r={5} />
                        <Bone w="30%" h={12} />
                        <div style={{ flex: 1 }} />
                        <Bone w={60} h={12} />
                        {!isMobile && <Bone w={60} h={12} />}
                    </div>
                ))}
            </div>
        );
    } else if (rows.length === 0) {
        body = <SectionEmpty kpiName={copy.title.toLowerCase()} minHeight={isMobile ? 180 : 240} />;
    } else {
        body = (
            <>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isMobile ? 8 : 12 }}>
                    <DoughnutChart
                        data={donutData}
                        kpiName={copy.title.toLowerCase()}
                        centerLabel="Net sales"
                        centerValue={formatCenter(totals.netSales)}
                        formatValue={formatMoney}
                        chartDiameter={donutSize}
                        minHeight={donutSize}
                        hideLegend
                        pctDecimals={1}
                    />
                </div>
                {isMobile ? (
                    <MobileTable rows={rows} totals={totals} formatMoney={formatMoney} fmtCount={fmtCount} hint={comparisonHint} showInfo={view === 'source'} />
                ) : (
                    <DesktopTable rows={rows} totals={totals} column={copy.column} formatMoney={formatMoney} fmtCount={fmtCount} hint={comparisonHint} showInfo={view === 'source'} />
                )}
            </>
        );
    }

    return (
        <div style={{ fontFamily: T.font }}>
            {header}
            {body}
        </div>
    );
}

/* ── Desktop: one row per bucket, five columns ──────────────────────────── */
const DESKTOP_COLS = 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 0.9fr)';

function DesktopTable({ rows, totals, column, formatMoney, fmtCount, hint, showInfo }: {
    rows: BreakdownRow[]; totals: ReturnType<typeof buildTotals>; column: string;
    formatMoney: (v: number) => string; fmtCount: (n: number) => string; hint?: string; showInfo?: boolean;
}) {
    const head: React.CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: T.t3, textAlign: 'right', alignSelf: 'end' };
    const cell: React.CSSProperties = { fontSize: 13, color: T.t2, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
    return (
        <div role="table">
            <div role="row" style={{ display: 'grid', gridTemplateColumns: DESKTOP_COLS, gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.divider}` }}>
                <div style={{ ...head, textAlign: 'left' }}>{column}</div>
                <div style={head}>Net sales</div>
                <div style={head}>Total collected</div>
                <div style={head}>Orders</div>
                <div style={head}>Avg order</div>
            </div>
            {rows.map((r) => (
                <div key={r.key} role="row" style={{ display: 'grid', gridTemplateColumns: DESKTOP_COLS, gap: 10, alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${T.divider}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <Marker color={r.color} />
                        {/* Ellipsised when cramped; full name in a tooltip on hover (SBS-04) */}
                        <TruncatedText text={r.name} style={{ fontSize: 13, color: T.t1 }} />
                        {showInfo && getSourceInfoText(r.name) && <KpiTooltip text={getSourceInfoText(r.name)!} />}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{formatMoney(r.netSales)}</div>
                        <Delta pct={r.deltaPct} hint={hint} size={11} />
                    </div>
                    <div style={cell}>{formatMoney(r.totalCollected)}</div>
                    <div style={cell}>{fmtCount(r.orders)}</div>
                    <div style={cell}>{formatMoney(r.avgOrder)}</div>
                </div>
            ))}
            <div role="row" style={{ display: 'grid', gridTemplateColumns: DESKTOP_COLS, gap: 10, alignItems: 'center', padding: '14px 0 2px', borderTop: `2px solid ${T.divider}`, marginTop: -1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>Total</div>
                <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{formatMoney(totals.netSales)}</div>
                <div style={cell}>{formatMoney(totals.totalCollected)}</div>
                <div style={cell}>{fmtCount(totals.orders)}</div>
                <div style={cell}>{formatMoney(totals.avgOrder)}</div>
            </div>
        </div>
    );
}

/* ── Mobile: name on its own line, the four figures beneath it ─────────────
   The totals line carries no "Total" label — it sat on a line of its own and
   read as mis-aligned against the figure columns (ZAR-2371 review). The heavier
   top rule marks it as the total. */
const MOBILE_COLS = 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 1fr)';
const MOBILE_INDENT = 20;

function MobileTable({ rows, totals, formatMoney, fmtCount, hint, showInfo }: {
    rows: BreakdownRow[]; totals: ReturnType<typeof buildTotals>;
    formatMoney: (v: number) => string; fmtCount: (n: number) => string; hint?: string; showInfo?: boolean;
}) {
    const head: React.CSSProperties = { fontSize: 10, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', color: T.t3 };
    const cell: React.CSSProperties = { fontSize: 12, color: T.t2, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: MOBILE_COLS, gap: 6, paddingLeft: MOBILE_INDENT };
    return (
        <div role="table">
            <div role="row" style={{ ...grid, padding: `6px 0 6px ${MOBILE_INDENT}px`, borderBottom: `1px solid ${T.divider}` }}>
                <div style={head}>Net sales</div>
                <div style={head}>Collected</div>
                <div style={head}>Orders</div>
                <div style={head}>Avg order</div>
            </div>
            {rows.map((r) => (
                <div key={r.key} role="row" style={{ padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <Marker color={r.color} />
                        <span style={{ fontSize: 13, color: T.t1 }}>{r.name}</span>
                        {showInfo && getSourceInfoText(r.name) && <KpiTooltip text={getSourceInfoText(r.name)!} isMobile />}
                    </div>
                    <div style={{ ...grid, alignItems: 'start' }}>
                        <div>
                            <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{formatMoney(r.netSales)}</div>
                            <Delta pct={r.deltaPct} hint={hint} size={10} />
                        </div>
                        <div style={cell}>{formatMoney(r.totalCollected)}</div>
                        <div style={cell}>{fmtCount(r.orders)}</div>
                        <div style={cell}>{formatMoney(r.avgOrder)}</div>
                    </div>
                </div>
            ))}
            <div role="row" aria-label="Total" style={{ ...grid, padding: `12px 0 2px ${MOBILE_INDENT}px`, borderTop: `2px solid ${T.divider}`, marginTop: -1 }}>
                <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{formatMoney(totals.netSales)}</div>
                <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{formatMoney(totals.totalCollected)}</div>
                <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{fmtCount(totals.orders)}</div>
                <div style={{ ...cell, fontWeight: 700, color: T.t1 }}>{formatMoney(totals.avgOrder)}</div>
            </div>
        </div>
    );
}
